import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/modal";
import { toast } from "@/components/ui/sonner";

import { CatalogueWriteError, useDeleteItem, useDeletionBlockers } from "../api";
import type { Blocker, CatalogueItem, CatalogueKind } from "../types";

/**
 * Delete a row — or explain why it cannot be.
 *
 * The server refuses any deletion that would take member data, or another
 * item's filter, with it; the blockers are asked for first so the refusal is
 * shown before any button is offered. A refusal that arrives at deletion time
 * (something changed since) is shown the same way.
 */
export function DeleteDialog({
  kind,
  item,
  onClose,
}: {
  kind: CatalogueKind;
  item: CatalogueItem;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const blockersQuery = useDeletionBlockers(kind, item.id);
  const remove = useDeleteItem(kind);
  const [lateBlockers, setLateBlockers] = useState<Blocker[] | null>(null);
  const [failed, setFailed] = useState(false);

  const blockers = lateBlockers ?? blockersQuery.data ?? [];

  const confirm = async () => {
    setFailed(false);
    try {
      await remove.mutateAsync(item.id);
      toast.success(t("catalogue.deleted", { name: item.name }));
      onClose();
    } catch (error) {
      if (error instanceof CatalogueWriteError && error.blockers) {
        setLateBlockers(error.blockers);
      } else {
        setFailed(true);
      }
    }
  };

  return (
    <Modal
      title={t("catalogue.deletion.title", { name: item.name })}
      onClose={onClose}
      className="max-w-lg"
    >
      <div className="space-y-3 p-4 text-sm">
        {blockersQuery.isPending && !lateBlockers ? (
          <p className="text-muted-foreground">{t("catalogue.loading")}</p>
        ) : blockersQuery.isError && !lateBlockers ? (
          <p role="alert" className="text-destructive-ink">
            {t("catalogue.error")}
          </p>
        ) : blockers.length > 0 ? (
          <div role="alert" data-deletion-blockers>
            <p className="font-semibold text-destructive-ink">
              {t("catalogue.deletion.blocked")}
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {blockers.map((blocker) => (
                <li key={blocker.label}>
                  {blocker.label}{" "}
                  <span className="rounded bg-muted px-1.5 text-xs font-semibold">{blocker.count}</span>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-muted-foreground">
              {t("catalogue.deletion.blockedHelp")}
            </p>
          </div>
        ) : (
          <p>{t("catalogue.deletion.permanent")}</p>
        )}
        {failed && (
          <p role="alert" className="text-destructive-ink">
            {t("catalogue.error")}
          </p>
        )}
      </div>
      <div className="flex justify-end gap-2 border-t border-border p-4">
        <Button variant="outline" onClick={onClose}>
          {blockers.length > 0 ? t("catalogue.close") : t("catalogue.cancel")}
        </Button>
        {!blockersQuery.isPending && !blockersQuery.isError && blockers.length === 0 && (
          <Button
            variant="destructive"
            onClick={() => void confirm()}
            disabled={remove.isPending}
          >
            {t("catalogue.delete")}
          </Button>
        )}
      </div>
    </Modal>
  );
}
